import React, { useContext, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ArrowLeft, PlusIcon } from "lucide-react";

import SharedDataContext from "../../../SharedDataContext";
import { viewTeam } from "../../../apis/workspace/teams";
import { getMembers } from "../../../apis/workspace/members";
import NavAnchor from "../../Tags/NavAnchor";
import GenericModal from "../../GenericModal";
import useModal from "../../GenericModal/useModal";
import TeamModal from "../Teams/TeamModal";
import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Badge } from "../../ui/badge";
import Image from "../../Tags/Image";
import AddMembersModal from "../Teams/AddMembersModal";
import ProductAccess from "./ProductAccess";
import TableLoader from "../../Loaders/TableLoader";
import TeamMembers from "./TeamMembers";
import PageHeader from "../../PageHeader";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const EMPTY_TEAM = {
  members: [],
  created_by: null,
};

export default function TeamDetails() {
  const captureEvent = useCaptureEvent();
  const queryClient = useQueryClient();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { teamId } = useParams();

  const { isOpen, openModal, closeModal } = useModal();
  const { isOpen: isOpenMember, openModal: openModalMember, closeModal: closeModalMember } = useModal();
  const {
    data: teamData,
    isLoading: isTeamLoading,
    isError: isTeamError,
    refetch: refetchTeam,
  } = useQuery(
    ["view_team", teamId, activeWorkspace?.id],
    () =>
      viewTeam({
        teamId,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      }),
    {
      enabled: Boolean(teamId && activeWorkspace?.id),
      select: (response) => response?.data ?? EMPTY_TEAM,
      staleTime: 30_000,
    }
  );

  const { data: teamMembers = [] } = useQuery(
    ["team_member_options", activeWorkspace?.id],
    () =>
      getMembers({
        query: "per_page=100",
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      }),
    {
      enabled: Boolean(activeWorkspace?.id),
      select: (response) => response?.data ?? [],
      staleTime: 300_000,
    }
  );

  const team = teamData ?? EMPTY_TEAM;
  const members = Array.isArray(team?.members) ? team.members : [];
  const totalMembers = team?.total_members ?? members.length ?? 0;

  if (isTeamLoading) {
    return <TableLoader />;
  }

  if (isTeamError) {
    return <div className="p-6 text-sm text-destructive">Unable to load team details.</div>;
  }

  if (!team || !team.id) {
    return null;
  }

  return (
    <>
      <div className="">
        <PageHeader goBack={true}>
          <div className="mb-2">
            <NavAnchor to={`/settings/teams/`} className="inline-flex items-center text-sm font-medium">
              <ArrowLeft className="h-4 w-4 text-gray-600" />
              <span className="ml-2 text-gray-600">All Teams</span>
            </NavAnchor>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                {team.name}

                <Button
                  size="sm"
                  data-tooltip-id="in_table_actions"
                  data-tooltip-content="Edit"
                  variant="ghost"
                  onClick={() => {
                    openModal();
                    captureEvent("SET_TeamDetails_EditTeamClick", {
                      team_id: team?.id,
                      team_name: team?.name,
                    });
                  }}
                  className="ml-2"
                >
                  <Image
                    src="/images/icons/untitledui/edit.svg"
                    width="16"
                    height="16"
                    className="inline-block"
                  />
                </Button>
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {team.description || "No description provided for this team."}
              </p>
            </div>

              <div className="flex flex-col items-end gap-3">
                <Button onClick={() => {
                  openModalMember();
                  captureEvent("SET_TeamDetails_AddMemberClick", {
                    team_id: team?.id,
                    team_name: team?.name,
                  });
                }}>
                  <PlusIcon className="h-5 w-5 mr-2" aria-hidden="true" />
                  Add team member
                </Button>
              </div>
          </div>
        </PageHeader>

        <div>
          <Tabs defaultValue="members" className="w-full mt-6" onValueChange={(tab) => {
            captureEvent("SET_TeamDetails_TabChange", {
              team_id: team?.id,
              tab: tab,
            });
          }}>
            <TabsList className="w-full sm:w-auto">
              <TabsTrigger value="members">
                <span className="flex items-center">
                  Members
                  {totalMembers > 0 ? <Badge className="ml-2">{totalMembers}</Badge> : null}
                </span>
              </TabsTrigger>
              <TabsTrigger value="product_access">Product Access</TabsTrigger>
            </TabsList>
            <TabsContent value="members" className="mt-6">
              <TeamMembers />
            </TabsContent>
            <TabsContent value="product_access" className="mt-6">
              <ProductAccess />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <GenericModal isOpen={isOpen} closeModal={closeModal}>
        <TeamModal
          closeModal={closeModal}
          activeWorkspace={activeWorkspace}
          edit={true}
          data={team}
          teamMembers={teamMembers}
          refetch={refetchTeam}
        />
      </GenericModal>
      <GenericModal isOpen={isOpenMember} closeModal={closeModalMember}>
        <AddMembersModal
          closeModal={closeModalMember}
          activeWorkspace={activeWorkspace}
          data={team}
          teamMembers={teamMembers}
          refetch={() => {
            refetchTeam();
            queryClient.invalidateQueries("get_members_of_team");
          }}
        />
      </GenericModal>
    </>
  );
}
